import Markdown from 'react-markdown';
import {
  formatCounselPanelHtml,
  isCounselDraftPlaceholder,
  parseCounselPanelSections,
} from '../../lib/formatCounselPanelHtml';
import { hasVisibleCounselStreamText } from '../../lib/counselStreamVisibility';
import { PANEL_BODY, counselProseClass } from './lexCounselPanelUi';
import { useLexCounselStore } from '../../store/lexCounselStore';

function stripPartialHtml(text: string): string {
  return text
    .replace(/<[^>]*$/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

type Props = {
  text: string;
  streaming?: boolean;
};

function CounselSectionBody({ html }: { html: string }) {
  return (
    <div
      className="counsel-section-body"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function CounselFormattedAnswer({ html }: { html: string }) {
  const sections = parseCounselPanelSections(html);
  const visibleSections = sections.filter(
    (section) =>
      section.kind !== 'draft' ||
      (section.html.trim() && !isCounselDraftPlaceholder(section.html)),
  );

  if (visibleSections.length === 0) {
    return null;
  }

  if (visibleSections.length === 1 && visibleSections[0].kind === 'body') {
    return (
      <div className="lexcounsel-content counsel-panel-answer">
        <CounselSectionBody html={visibleSections[0].html} />
      </div>
    );
  }

  return (
    <div className="lexcounsel-content counsel-panel-answer space-y-3">
      {visibleSections.map((section, index) => {
        const isBrief = section.kind === 'brief' || section.kind === 'body';
        return (
          <section
            key={`${section.kind}-${index}`}
            className={
              isBrief ? 'counsel-panel-section counsel-panel-brief' : 'counsel-panel-section counsel-panel-draft'
            }
          >
            {isBrief ? (
              <header className="counsel-panel-section-label">Response</header>
            ) : (
              <header className="counsel-panel-section-label">Draft text</header>
            )}
            <CounselSectionBody html={section.html} />
          </section>
        );
      })}
    </div>
  );
}

export default function LexCounselStreamingContent({ text, streaming }: Props) {
  const panelTheme = useLexCounselStore((s) => s.panelTheme);

  if (!text) return null;

  if (streaming) {
    const looksHtml = /<[a-z][\s\S]*>/i.test(text);
    if (looksHtml) {
      const partial = stripPartialHtml(text);
      if (partial.length >= 8) {
        return (
          <div className={`lexcounsel-content counsel-panel-streaming ${PANEL_BODY}`}>
            <p>{partial}</p>
            <span className="counsel-stream-cursor" aria-hidden />
          </div>
        );
      }
      const formatted = formatCounselPanelHtml(text);
      if (hasVisibleCounselStreamText(formatted)) {
        return (
          <div className={`lexcounsel-content counsel-panel-streaming ${PANEL_BODY}`}>
            <CounselFormattedAnswer html={formatted} />
            <span className="counsel-stream-cursor" aria-hidden />
          </div>
        );
      }
      return null;
    }
    return (
      <div
        className={`lexcounsel-content counsel-panel-streaming ${counselProseClass(panelTheme)} ${PANEL_BODY}`}
      >
        <Markdown>{text}</Markdown>
        <span className="counsel-stream-cursor" aria-hidden />
      </div>
    );
  }

  const formatted = formatCounselPanelHtml(text);
  if (!formatted) return null;

  return <CounselFormattedAnswer html={formatted} />;
}
